/**
 * "Nytt ferieår" on Saldo (owner decision D32 B, 2026-09-22): a preview of every person's move into
 * the new vacation year, the days carried editable downwards, and one confirmed move. The server
 * (controllers/admin/vacationYearController.js) moves each person once and logs it. Ferieloven §7(3):
 * days not taken for sickness, parental leave or the employer's doing must be carried over, so every
 * unused day is carried unless the administrator lowers it.
 */
import { type VacationYearRow, useVacationYear, useVacationYearApply } from '@/api/data';
import { T } from '@/i18n';
import { Button, Drawer, toast } from '@/ui';
import { num } from '@/ui/format';
import { useState } from 'react';

export function NyttFerieaar({ year }: { year: number }) {
  const [open, setOpen] = useState(false);
  const [carry, setCarry] = useState<Record<number, string>>({});
  const q = useVacationYear(year, open);
  const apply = useVacationYearApply();
  const rows = q.data?.rows ?? [];
  const carried = (r: VacationYearRow) => {
    const v = carry[r.user_id];
    const n = v == null || v === '' ? r.carry : Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : Number.NaN;
  };
  const bad = rows.filter((r) => !r.done && !(carried(r) >= 0 && carried(r) <= r.left));
  const waiting = rows.filter((r) => !r.done).length;
  const run = async () => {
    const body: Record<number, number> = {};
    for (const r of rows) if (!r.done) body[r.user_id] = carried(r);
    try {
      const res = await apply.mutateAsync({ year, carry: body });
      toast(T('{n} ansatte flyttet til ferieåret {year}', { n: res.moved ?? 0, year }));
      setOpen(false);
      setCarry({});
    } catch (e) {
      toast(`${T('Feilet')}: ${(e as Error).message}`);
    }
  };
  return (
    <>
      <Button small onClick={() => setOpen(true)}>
        {T('Nytt ferieår {year}', { year })}
      </Button>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={T('Nytt ferieår {year}', { year })}
        sub={T('Ubrukte dager overføres, og neste år starter på den enkeltes rettighet.')}
        wide
        footer={
          <Button
            tone="pri"
            disabled={!q.data?.can_apply || !waiting || bad.length > 0 || apply.isPending}
            onClick={run}
          >
            {T('Flytt {n} ansatte til {year}', { n: waiting, year })}
          </Button>
        }
      >
        {q.data && !q.data.can_apply && (
          <p className="hint" data-testid="ferieaar-early">
            {T('Kan kjøres fra 1. januar {year}. Til da er dette en forhåndsvisning.', { year })}
          </p>
        )}
        <table className="t" data-testid="ferieaar">
          <thead>
            <tr>
              <th>{T('Ansatt')}</th>
              <th className="n">{T('Igjen fra {y}', { y: year - 1 })}</th>
              <th className="n">{T('Overføres')}</th>
              <th className="n">{T('Saldo {y}', { y: year })}</th>
              <th className="n">{T('Rettighet {y}', { y: year + 1 })}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} data-testid="ferieaar-row">
                <td>{r.name}</td>
                <td className="n">{num(r.left, 1)}</td>
                <td className="n">
                  {r.done ? (
                    T('flyttet')
                  ) : (
                    <input
                      className="inp"
                      style={{ width: 70 }}
                      inputMode="decimal"
                      aria-label={T('Overføres for {name}', { name: r.name })}
                      aria-invalid={bad.includes(r)}
                      value={carry[r.user_id] ?? String(r.carry)}
                      onChange={(e) => setCarry({ ...carry, [r.user_id]: e.target.value })}
                    />
                  )}
                </td>
                <td className="n">{r.done ? '–' : num(r.next_year_balance + (carried(r) || 0), 1)}</td>
                <td className="n">{r.entitlement}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hint" style={{ maxWidth: '66ch' }}>
          {T(
            'Ferie som ikke ble tatt på grunn av sykdom, foreldrepermisjon eller fordi arbeidsgiveren ikke la den til rette, skal overføres (ferieloven § 7). Alle ubrukte dager er foreslått overført; senk tallet der dere har avtalt noe annet.',
          )}
        </p>
      </Drawer>
    </>
  );
}
